import { useQuery } from "@tanstack/react-query";

import { customInstanceBpm } from "../axios-client";

export interface TaskDurationStatistic {
  taskDefinitionKey?: string;
  taskName?: string;
  executions?: number;
  completedExecutions?: number;
  totalDurationInMillis?: number;
  averageDurationInMillis?: number;
}

export interface ProcessDurationStatistic {
  processDefinitionKey?: string;
  processDefinitionName?: string;
  executions?: number;
  completedExecutions?: number;
  totalDurationInMillis?: number;
  averageDurationInMillis?: number;
}

export interface OldestActiveProcess {
  processInstanceId?: string;
  processDefinitionKey?: string;
  processDefinitionName?: string;
  startTime?: string;
  ageInMillis?: number;
}

export interface ProcessTrendStatistic {
  startDate?: string;
  endDate?: string;
  startedInstances?: number;
  completedInstances?: number;
}

export interface IncidentStatistic {
  processDefinitionKey?: string;
  processDefinitionName?: string;
  totalCount?: number;
  openCount?: number;
  closedCount?: number;
}

export type RecentActivityType =
  | "PROCESS_STARTED"
  | "PROCESS_COMPLETED"
  | "TASK_COMPLETED";

export interface RecentActivity {
  type?: RecentActivityType;
  processInstanceId?: string;
  processDefinitionKey?: string;
  processDefinitionName?: string;
  taskName?: string;
  occurredAt?: string;
}

export interface ProcessDashboardData {
  processDefinitionId?: string;
  processDefinitionKey?: string;
  processDefinitionName?: string;
  periodDays?: number;
  periodStartDate?: string;
  periodEndDate?: string;
  totalInstances?: number;
  activeInstances?: number;
  completedInstances?: number;
  totalDurationInMillis?: number;
  averageProcessDurationInMillis?: number;
  averageTaskDurationInMillis?: number;
  completionRate?: number;
  documentsCount?: number;
  signaturesCount?: number;
  incidentsCount?: number;
  incidentProcessesCount?: number;
  criticalIncidentsCount?: number;
  pendingTasksCount?: number;
  overdueTasksCount?: number;
  tasksNearDeadlineCount?: number;
  tasksWithoutDueDateCount?: number;
  pendingTasksUpTo7DaysCount?: number;
  pendingTasksFrom8To15DaysCount?: number;
  pendingTasksFrom16To30DaysCount?: number;
  pendingTasksOver30DaysCount?: number;
  nearDeadlineDays?: number;
  averagePendingTaskWaitInMillis?: number;
  completedTasksCount?: number;
  averageCompletedTasksPerDay?: number;
  slaComplianceRate?: number;
  slaCompliantTasksCount?: number;
  slaViolatedTasksCount?: number;
  reworkedInstancesCount?: number;
  reworkRate?: number;
  oldestActiveProcess?: OldestActiveProcess;
  bottleneckTask?: TaskDurationStatistic;
  processDurations?: ProcessDurationStatistic[];
  processTrend?: ProcessTrendStatistic[];
  recentActivities?: RecentActivity[];
  incidentStatistics?: IncidentStatistic[];
}

export interface ProcessDashboardParams {
  startDate: string;
  endDate: string;
  userId?: string;
  groupId?: string;
  timeZone?: string;
}

export const getProcessDashboard = ({
  startDate,
  endDate,
  timeZone,
  userId,
  groupId,
}: ProcessDashboardParams) => {
  const query = new URLSearchParams({ startDate, endDate });
  if (timeZone) query.set("timeZone", timeZone);
  if (userId) {
    query.set("userId", userId);
  }
  if (groupId) {
    query.set("groupId", groupId);
  }

  return customInstanceBpm<ProcessDashboardData>(
    `/smart-bpm/rest/process-definition/dashboard?${query.toString()}`,
    { method: "GET" },
  );
};

export const useGetProcessDashboard = (
  params: ProcessDashboardParams,
  enabled: boolean,
) => {
  // Build a stable, deterministic query key containing only the defined values.
  // This ensures React Query creates a new cache entry whenever any filter changes,
  // including when optional fields like userId/groupId toggle between defined and undefined.
  const stableKey = {
    startDate: params.startDate,
    endDate: params.endDate,
    ...(params.userId ? { userId: params.userId } : {}),
    ...(params.groupId ? { groupId: params.groupId } : {}),
  };

  return useQuery({
    queryKey: ["process-dashboard", stableKey],
    queryFn: () => getProcessDashboard(params),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};
